import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit, Globe, ClipboardList, GripVertical, Save, Eye, Share2, Download, Building2, Calendar, Clock, Lock, Columns2, Columns3, FileText, SeparatorHorizontal, MessageSquare, Pencil, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: 'T' },
  { value: 'textarea', label: 'Long Text', icon: 'P' },
  { value: 'email', label: 'Email', icon: '@' },
  { value: 'tel', label: 'Phone', icon: '#' },
  { value: 'number', label: 'Number', icon: '1' },
  { value: 'date', label: 'Date', icon: 'D' },
  { value: 'time', label: 'Time', icon: 'T' },
  { value: 'select', label: 'Dropdown', icon: 'V' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑' },
  { value: 'checkbox_conditional', label: 'Checkbox + Conditional Field', icon: '☑+' },
  { value: 'repeater', label: '2-Column Repeater', icon: '2' },
  { value: 'repeater3', label: '3-Column Repeater', icon: '3' },
  { value: 'columns2', label: '2-Column Layout', icon: '||' },
  { value: 'columns3', label: '3-Column Layout', icon: '|||' },
  { value: 'section', label: 'Section/Page Break', icon: '—' },
  { value: 'comment', label: 'Comment/Note', icon: '?' }
];

// Conditional field types for checkbox_conditional
const CONDITIONAL_FIELD_TYPES = [
  { value: 'text', label: 'Text Field' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' }
];

// Column type options for multi-column layouts
const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' }
];

// Sortable Field Item Component
const SortableFieldItem = ({ field, index, isEditing, onRemove, onEdit, editingFieldIndex, setEditingFieldIndex, onSaveEdit, editFieldData, setEditFieldData }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || `field-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCurrentlyEditing = editingFieldIndex === index;
  const fieldTypeInfo = FIELD_TYPES.find(t => t.value === field.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        isDragging ? 'bg-primary/10 border-primary shadow-lg' : 'bg-muted/10 border-border hover:border-muted-foreground/30'
      } ${field.type === 'section' ? 'border-dashed border-orange-500/50 bg-orange-500/5' : ''} 
        ${field.type === 'comment' ? 'border-dashed border-blue-500/50 bg-blue-500/5' : ''}`}
      data-testid={`field-item-${index}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/30 rounded mt-1"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      
      {isCurrentlyEditing ? (
        // Inline Edit Mode
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={editFieldData.label}
              onChange={(e) => setEditFieldData({ ...editFieldData, label: e.target.value })}
              placeholder="Field Label"
              className="bg-background border-border text-foreground h-8 text-sm"
              data-testid="edit-field-label-input"
            />
            <Select 
              value={editFieldData.type} 
              onValueChange={(v) => setEditFieldData({ ...editFieldData, type: v })}
            >
              <SelectTrigger className="bg-background border-border text-foreground h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {FIELD_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(editFieldData.type === 'comment' || editFieldData.type === 'section') ? (
            <Textarea
              value={editFieldData.placeholder || ''}
              onChange={(e) => setEditFieldData({ ...editFieldData, placeholder: e.target.value })}
              placeholder={editFieldData.type === 'comment' ? 'Enter note text that describes this section...' : 'Section title (optional)'}
              className="bg-background border-border text-foreground text-sm min-h-[60px]"
            />
          ) : (
            <Input
              value={editFieldData.placeholder || ''}
              onChange={(e) => setEditFieldData({ ...editFieldData, placeholder: e.target.value })}
              placeholder="Placeholder text"
              className="bg-background border-border text-foreground h-8 text-sm"
            />
          )}
          {editFieldData.type === 'columns2' && (
            <div className="grid grid-cols-2 gap-2 p-2 bg-purple-500/10 rounded border border-purple-500/30">
              <Input
                value={editFieldData.column1Label || ''}
                onChange={(e) => setEditFieldData({ ...editFieldData, column1Label: e.target.value })}
                placeholder="Column 1 Label"
                className="bg-background border-border text-foreground h-8 text-sm"
              />
              <Input
                value={editFieldData.column2Label || ''}
                onChange={(e) => setEditFieldData({ ...editFieldData, column2Label: e.target.value })}
                placeholder="Column 2 Label"
                className="bg-background border-border text-foreground h-8 text-sm"
              />
            </div>
          )}
          {editFieldData.type === 'columns3' && (
            <div className="grid grid-cols-3 gap-2 p-2 bg-primary/10 rounded border border-primary/30">
              <Input
                value={editFieldData.column1Label || ''}
                onChange={(e) => setEditFieldData({ ...editFieldData, column1Label: e.target.value })}
                placeholder="Column 1 Label"
                className="bg-background border-border text-foreground h-8 text-sm"
              />
              <Input
                value={editFieldData.column2Label || ''}
                onChange={(e) => setEditFieldData({ ...editFieldData, column2Label: e.target.value })}
                placeholder="Column 2 Label"
                className="bg-background border-border text-foreground h-8 text-sm"
              />
              <Input
                value={editFieldData.column3Label || ''}
                onChange={(e) => setEditFieldData({ ...editFieldData, column3Label: e.target.value })}
                placeholder="Column 3 Label"
                className="bg-background border-border text-foreground h-8 text-sm"
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            {field.type !== 'section' && field.type !== 'comment' && (
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={editFieldData.required || false}
                  onChange={(e) => setEditFieldData({ ...editFieldData, required: e.target.checked })}
                  className="rounded"
                />
                Required
              </label>
            )}
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="ghost" onClick={() => setEditingFieldIndex(null)} className="h-7 px-2">
                <X className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => onSaveEdit(index)} className="h-7 px-2 bg-green-600 hover:bg-green-700">
                <Check className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // Display Mode
        <>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{field.label}</span>
              <span className="text-xs px-2 py-0.5 bg-muted/30 rounded">{fieldTypeInfo?.label || field.type}</span>
              {field.required && field.type !== 'section' && field.type !== 'comment' && (
                <span className="text-xs text-neon-blue">*required</span>
              )}
            </div>
            {field.placeholder && (
              <p className="text-xs text-muted-foreground mt-1">
                {field.type === 'comment' ? field.placeholder : `Placeholder: ${field.placeholder}`}
              </p>
            )}
            {field.options?.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">Options: {field.options.join(', ')}</p>
            )}
            {field.type === 'columns2' && field.column1Label && field.column2Label && (
              <p className="text-xs text-neon-purple mt-1">
                Columns: {field.column1Label} ({field.column1Type || 'text'}) | {field.column2Label} ({field.column2Type || 'text'})
              </p>
            )}
            {field.type === 'columns3' && field.column1Label && field.column2Label && field.column3Label && (
              <p className="text-xs text-neon-blue mt-1">
                Columns: {field.column1Label} ({field.column1Type || 'text'}) | {field.column2Label} ({field.column2Type || 'text'}) | {field.column3Label} ({field.column3Type || 'text'})
              </p>
            )}
            {field.type === 'repeater' && field.column1Label && field.column2Label && (
              <p className="text-xs text-neon-purple mt-1">
                Columns: {field.column1Label} | {field.column2Label}
              </p>
            )}
            {field.type === 'repeater3' && field.column1Label && field.column2Label && field.column3Label && (
              <p className="text-xs text-neon-blue mt-1">
                Columns: {field.column1Label} | {field.column2Label} | {field.column3Label}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(index)}
              className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
              data-testid={`edit-field-btn-${index}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onRemove(index)}
              className="text-red-500 hover:text-red-400 h-8 w-8 p-0"
              data-testid={`remove-field-btn-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default function FormsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [forms, setForms] = useState([]);
  const [sharedForms, setSharedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-forms');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [newForm, setNewForm] = useState({
    name: '',
    description: '',
    fields: [],
    is_shared: false,
    lockout_days_before_event: '',
    lockout_days_after_event: ''
  });
  const [newField, setNewField] = useState({
    label: '',
    name: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: [],
    column1Label: '',
    column2Label: '',
    column3Label: '',
    column1Type: 'text',
    column2Type: 'text',
    column3Type: 'text',
    column1Options: [],
    column2Options: [],
    column3Options: [],
    // Conditional checkbox fields
    conditionalFieldLabel: '',
    conditionalFieldType: 'text',
    conditionalFieldPlaceholder: '',
    conditionalFieldOptions: []
  });
  const [optionInput, setOptionInput] = useState('');
  const [column1OptionInput, setColumn1OptionInput] = useState('');
  const [column2OptionInput, setColumn2OptionInput] = useState('');
  const [column3OptionInput, setColumn3OptionInput] = useState('');
  const [conditionalOptionInput, setConditionalOptionInput] = useState('');
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [editFieldData, setEditFieldData] = useState({});

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchForms();
    fetchSharedForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/planning-forms`);
      // Sort to put contact form first
      const sortedForms = response.data.sort((a, b) => {
        if (a.form_type === 'contact') return -1;
        if (b.form_type === 'contact') return 1;
        return 0;
      });
      setForms(sortedForms);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedForms = async () => {
    try {
      const response = await axios.get(`${API}/planning-forms/shared`);
      setSharedForms(response.data);
    } catch (error) {
      console.error('Failed to fetch shared forms:', error);
    }
  };

  const toggleFormSharing = async (formId, isShared) => {
    try {
      await axios.put(`${API}/planning-forms/${formId}/share`, { is_shared: isShared });
      toast.success(isShared ? 'Form is now shared with other companies' : 'Form is no longer shared');
      fetchForms();
    } catch (error) {
      toast.error('Failed to update sharing status');
    }
  };

  const importSharedForm = async (formId) => {
    try {
      const response = await axios.post(`${API}/planning-forms/import/${formId}`);
      toast.success(response.data.message);
      fetchForms();
      fetchSharedForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import form');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreate = async () => {
    if (!newForm.name) {
      toast.error('Please enter a form name');
      return;
    }
    if (newForm.fields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }

    try {
      const formPayload = {
        ...newForm,
        lockout_days_before_event: newForm.lockout_days_before_event ? parseInt(newForm.lockout_days_before_event) : null,
        lockout_days_after_event: newForm.lockout_days_after_event ? parseInt(newForm.lockout_days_after_event) : null
      };
      await axios.post(`${API}/planning-forms`, formPayload);
      toast.success('Form created successfully');
      setShowCreateDialog(false);
      setNewForm({ name: '', description: '', fields: [], is_shared: false, lockout_days_before_event: '', lockout_days_after_event: '' });
      fetchForms();
    } catch (error) {
      toast.error('Failed to create form');
    }
  };

  const handleUpdate = async () => {
    if (!editingForm) return;
    
    try {
      await axios.put(`${API}/planning-forms/${editingForm.id}`, {
        name: editingForm.name,
        description: editingForm.description,
        fields: editingForm.fields,
        lockout_days_before_event: editingForm.lockout_days_before_event ? parseInt(editingForm.lockout_days_before_event) : null,
        lockout_days_after_event: editingForm.lockout_days_after_event ? parseInt(editingForm.lockout_days_after_event) : null
      });
      toast.success('Form updated successfully');
      setShowEditDialog(false);
      setEditingForm(null);
      fetchForms();
    } catch (error) {
      toast.error('Failed to update form');
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;
    
    try {
      await axios.delete(`${API}/planning-forms/${formId}`);
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      if (error.response?.data?.detail === 'System forms cannot be deleted') {
        toast.error('System forms cannot be deleted');
      } else {
        toast.error('Failed to delete form');
      }
    }
  };

  const openEditDialog = (form) => {
    setEditingForm({ ...form, fields: [...form.fields] });
    setShowEditDialog(true);
  };

  const addField = (isEditing = false) => {
    // For section and comment types, label can be optional or auto-generated
    if (!newField.label && newField.type !== 'section' && newField.type !== 'comment') {
      toast.error('Please enter a field label');
      return;
    }
    
    // Set default labels for section and comment if not provided
    if (newField.type === 'section' && !newField.label) {
      newField.label = 'New Section';
    }
    if (newField.type === 'comment' && !newField.label) {
      newField.label = 'Note';
    }
    
    // Validate repeater fields have column labels
    if (newField.type === 'repeater' && (!newField.column1Label || !newField.column2Label)) {
      toast.error('Please enter labels for both columns');
      return;
    }
    
    // Validate 3-column repeater fields have all column labels
    if (newField.type === 'repeater3' && (!newField.column1Label || !newField.column2Label || !newField.column3Label)) {
      toast.error('Please enter labels for all three columns');
      return;
    }
    
    // Validate 2-column layout fields have column labels
    if (newField.type === 'columns2' && (!newField.column1Label || !newField.column2Label)) {
      toast.error('Please enter labels for both columns');
      return;
    }
    
    // Validate 3-column layout fields have all column labels
    if (newField.type === 'columns3' && (!newField.column1Label || !newField.column2Label || !newField.column3Label)) {
      toast.error('Please enter labels for all three columns');
      return;
    }
    
    // Validate checkbox_conditional has a conditional field label
    if (newField.type === 'checkbox_conditional' && !newField.conditionalFieldLabel) {
      toast.error('Please enter a label for the conditional field');
      return;
    }
    
    const fieldName = newField.name || newField.label.toLowerCase().replace(/\s+/g, '_');
    const field = {
      ...newField,
      name: fieldName,
      id: `field-${Date.now()}` // Unique ID for drag-and-drop
    };
    
    if (isEditing && editingForm) {
      setEditingForm({
        ...editingForm,
        fields: [...editingForm.fields, field]
      });
    } else {
      setNewForm({
        ...newForm,
        fields: [...newForm.fields, field]
      });
    }
    
    setNewField({ 
      label: '', 
      name: '', 
      type: 'text', 
      required: false, 
      placeholder: '', 
      options: [], 
      column1Label: '', 
      column2Label: '', 
      column3Label: '',
      column1Type: 'text',
      column2Type: 'text',
      column3Type: 'text',
      column1Options: [],
      column2Options: [],
      column3Options: [],
      conditionalFieldLabel: '',
      conditionalFieldType: 'text',
      conditionalFieldPlaceholder: '',
      conditionalFieldOptions: []
    });
    setOptionInput('');
    setColumn1OptionInput('');
    setColumn2OptionInput('');
    setColumn3OptionInput('');
  };

  const removeField = (index, isEditing = false) => {
    if (isEditing && editingForm) {
      setEditingForm({
        ...editingForm,
        fields: editingForm.fields.filter((_, i) => i !== index)
      });
    } else {
      setNewForm({
        ...newForm,
        fields: newForm.fields.filter((_, i) => i !== index)
      });
    }
  };

  const updateEditingField = (index, updates) => {
    if (editingForm) {
      const newFields = [...editingForm.fields];
      newFields[index] = { ...newFields[index], ...updates };
      setEditingForm({ ...editingForm, fields: newFields });
    }
  };

  // Handle drag end for reordering fields
  const handleDragEnd = (event, isEditing = false) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const getIndex = (id, fields) => {
        return fields.findIndex((f, i) => (f.id || `field-${i}`) === id);
      };

      if (isEditing && editingForm) {
        const oldIndex = getIndex(active.id, editingForm.fields);
        const newIndex = getIndex(over.id, editingForm.fields);
        
        setEditingForm({
          ...editingForm,
          fields: arrayMove(editingForm.fields, oldIndex, newIndex)
        });
      } else {
        const oldIndex = getIndex(active.id, newForm.fields);
        const newIndex = getIndex(over.id, newForm.fields);
        
        setNewForm({
          ...newForm,
          fields: arrayMove(newForm.fields, oldIndex, newIndex)
        });
      }
    }
  };

  // Start editing a field inline
  const startEditField = (index, isEditing = false) => {
    const fields = isEditing ? editingForm?.fields : newForm.fields;
    if (fields && fields[index]) {
      setEditFieldData({ ...fields[index] });
      setEditingFieldIndex(index);
    }
  };

  // Save field edit
  const saveFieldEdit = (index, isEditing = false) => {
    if (!editFieldData.label && editFieldData.type !== 'section') {
      toast.error('Field label is required');
      return;
    }

    if (isEditing && editingForm) {
      const newFields = [...editingForm.fields];
      newFields[index] = { ...editFieldData, name: editFieldData.name || editFieldData.label.toLowerCase().replace(/\s+/g, '_') };
      setEditingForm({ ...editingForm, fields: newFields });
    } else {
      const newFields = [...newForm.fields];
      newFields[index] = { ...editFieldData, name: editFieldData.name || editFieldData.label.toLowerCase().replace(/\s+/g, '_') };
      setNewForm({ ...newForm, fields: newFields });
    }
    setEditingFieldIndex(null);
    setEditFieldData({});
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setNewField({
        ...newField,
        options: [...(newField.options || []), optionInput.trim()]
      });
      setOptionInput('');
    }
  };

  const removeOption = (index) => {
    setNewField({
      ...newField,
      options: newField.options.filter((_, i) => i !== index)
    });
  };

  const openPreview = (form) => {
    setPreviewForm(form);
    setShowPreviewDialog(true);
  };

  const renderFormPreview = (form) => {
    if (!form) return null;
    
    return (
      <div className="space-y-6">
        {form.fields?.map((field, index) => (
          <div key={index} className="space-y-2">
            {/* Section / Page Break */}
            {field.type === 'section' ? (
              <div className="py-4 my-4 border-t-2 border-dashed border-orange-500/50 print:break-before-page">
                <div className="flex items-center gap-2 text-orange-500">
                  <SeparatorHorizontal className="w-5 h-5" />
                  <span className="font-semibold text-lg">{field.label || 'New Section'}</span>
                </div>
                {field.placeholder && (
                  <p className="text-sm text-muted-foreground mt-1">{field.placeholder}</p>
                )}
              </div>
            ) : field.type === 'comment' ? (
              /* Comment / Note */
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-blue-400">{field.label}</span>
                    {field.placeholder && (
                      <p className="text-sm text-muted-foreground mt-1">{field.placeholder}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : field.type === 'columns2' ? (
              /* 2-Column Layout */
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
                  <Columns2 className="w-4 h-4" />
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{field.column1Label || 'Column 1'}</Label>
                    {field.column1Type === 'select' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                        <span>Select {field.column1Label?.toLowerCase() || 'option'}...</span>
                        <span className="text-xs">▼</span>
                      </div>
                    ) : field.column1Type === 'date' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Select date...</span>
                      </div>
                    ) : field.column1Type === 'time' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Select time...</span>
                      </div>
                    ) : (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                        Enter {field.column1Label?.toLowerCase() || 'value'}...
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{field.column2Label || 'Column 2'}</Label>
                    {field.column2Type === 'select' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                        <span>Select {field.column2Label?.toLowerCase() || 'option'}...</span>
                        <span className="text-xs">▼</span>
                      </div>
                    ) : field.column2Type === 'date' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Select date...</span>
                      </div>
                    ) : field.column2Type === 'time' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Select time...</span>
                      </div>
                    ) : (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                        Enter {field.column2Label?.toLowerCase() || 'value'}...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : field.type === 'columns3' ? (
              /* 3-Column Layout */
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
                  <Columns3 className="w-4 h-4" />
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{field.column1Label || 'Column 1'}</Label>
                    {field.column1Type === 'select' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                        <span>Select...</span>
                        <span className="text-xs">▼</span>
                      </div>
                    ) : field.column1Type === 'date' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Date</span>
                      </div>
                    ) : field.column1Type === 'time' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Time</span>
                      </div>
                    ) : (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                        Enter...
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{field.column2Label || 'Column 2'}</Label>
                    {field.column2Type === 'select' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                        <span>Select...</span>
                        <span className="text-xs">▼</span>
                      </div>
                    ) : field.column2Type === 'date' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Date</span>
                      </div>
                    ) : field.column2Type === 'time' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Time</span>
                      </div>
                    ) : (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                        Enter...
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{field.column3Label || 'Column 3'}</Label>
                    {field.column3Type === 'select' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                        <span>Select...</span>
                        <span className="text-xs">▼</span>
                      </div>
                    ) : field.column3Type === 'date' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Date</span>
                      </div>
                    ) : field.column3Type === 'time' ? (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Time</span>
                      </div>
                    ) : (
                      <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                        Enter...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : field.type === 'time' ? (
              /* Time Picker */
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
                  <Clock className="w-4 h-4" />
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                <div className="w-full max-w-[200px] p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{field.placeholder || 'Select time...'}</span>
                </div>
              </div>
            ) : (
              /* Standard fields */
              <>
                <Label className="flex items-center gap-1 text-sm font-medium text-foreground">
                  {field.label}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                {field.type === 'textarea' ? (
                  <div className="w-full min-h-[80px] p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                    {field.placeholder || 'Long text input...'}
                  </div>
                ) : field.type === 'select' ? (
                  <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm flex justify-between items-center">
                    <span>{field.placeholder || 'Select an option'}</span>
                    <span className="text-xs">▼</span>
                  </div>
                ) : field.type === 'repeater' ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs font-medium text-muted-foreground">
                      <span>{field.column1Label}</span>
                      <span>{field.column2Label}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-muted-foreground text-sm">
                        {field.column1Label}...
                      </div>
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-muted-foreground text-sm">
                        {field.column2Label}...
                      </div>
                    </div>
                    <div className="text-xs text-purple-500 text-center py-1 border border-dashed border-border rounded-lg">
                      + Add Row
                    </div>
                  </div>
                ) : field.type === 'repeater3' ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs font-medium text-muted-foreground">
                      <span>{field.column1Label}</span>
                      <span>{field.column2Label}</span>
                      <span>{field.column3Label}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-muted-foreground text-sm">
                        {field.column1Label}...
                      </div>
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-muted-foreground text-sm">
                        {field.column2Label}...
                      </div>
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-muted-foreground text-sm">
                        {field.column3Label}...
                      </div>
                    </div>
                    <div className="text-xs text-primary text-center py-1 border border-dashed border-border rounded-lg">
                      + Add Row
                    </div>
                  </div>
                ) : (
                  <div className="w-full p-3 bg-muted/30 border border-border rounded-xl text-muted-foreground text-sm">
                    {field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderFieldEditor = (isEditing = false) => (
    <div className="border border-border rounded-xl p-4 space-y-3">
      <Label className="text-sm font-semibold text-foreground">Add New Field</Label>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-foreground">Label *</Label>
          <Input
            value={newField.label}
            onChange={(e) => setNewField({ ...newField, label: e.target.value })}
            placeholder="e.g., Your Name"
            className="bg-background border-border text-foreground"
            data-testid="field-label-input"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-foreground">Type</Label>
          <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v })}>
            <SelectTrigger className="bg-background border-border text-foreground" data-testid="field-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-[300px]">
              <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Input Fields</div>
              {FIELD_TYPES.filter(t => ['text', 'textarea', 'email', 'tel', 'number', 'date', 'time', 'select'].includes(t.value)).map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-muted-foreground font-semibold border-t border-border mt-1 pt-1">Checkboxes</div>
              {FIELD_TYPES.filter(t => ['checkbox', 'checkbox_conditional'].includes(t.value)).map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-muted-foreground font-semibold border-t border-border mt-1 pt-1">Repeaters</div>
              {FIELD_TYPES.filter(t => ['repeater', 'repeater3'].includes(t.value)).map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-muted-foreground font-semibold border-t border-border mt-1 pt-1">Column Layouts</div>
              {FIELD_TYPES.filter(t => ['columns2', 'columns3'].includes(t.value)).map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-muted-foreground font-semibold border-t border-border mt-1 pt-1">Layout & Notes</div>
              {FIELD_TYPES.filter(t => ['section', 'comment'].includes(t.value)).map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Placeholder / Description field */}
      {newField.type === 'comment' ? (
        <div className="space-y-1">
          <Label className="text-xs text-foreground">Note/Comment Text</Label>
          <Textarea
            value={newField.placeholder}
            onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
            placeholder="Enter descriptive text that will help users understand this section..."
            className="bg-background border-border text-foreground min-h-[80px]"
          />
        </div>
      ) : newField.type === 'section' ? (
        <div className="space-y-1">
          <Label className="text-xs text-foreground">Section Description (optional)</Label>
          <Input
            value={newField.placeholder}
            onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
            placeholder="Brief description of what this section contains..."
            className="bg-background border-border text-foreground"
          />
        </div>
      ) : (
        <div className="space-y-1">
          <Label className="text-xs text-foreground">Placeholder (optional)</Label>
          <Input
            value={newField.placeholder}
            onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
            placeholder="Hint text shown in field"
            className="bg-background border-border text-foreground"
          />
        </div>
      )}
      
      {newField.type === 'select' && (
        <div className="space-y-2">
          <Label className="text-xs text-foreground">Dropdown Options</Label>
          <div className="flex gap-2">
            <Input
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
              placeholder="Add option..."
              className="bg-background border-border text-foreground flex-1"
            />
            <Button size="sm" onClick={addOption} type="button">Add</Button>
          </div>
          {newField.options?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {newField.options.map((opt, idx) => (
                <span key={idx} className="px-2 py-1 bg-muted/30 rounded-lg text-sm flex items-center gap-1 text-foreground">
                  {opt}
                  <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-300">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Checkbox Conditional Settings */}
      {newField.type === 'checkbox_conditional' && (
        <div className="space-y-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            <Label className="text-xs font-semibold text-green-400">Conditional Field Settings</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            When the checkbox is checked, an additional field will appear below it.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Conditional Field Label *</Label>
              <Input
                value={newField.conditionalFieldLabel}
                onChange={(e) => setNewField({ ...newField, conditionalFieldLabel: e.target.value })}
                placeholder="e.g., Please specify"
                className="bg-background border-border text-foreground h-8"
                data-testid="conditional-field-label"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Field Type</Label>
              <Select 
                value={newField.conditionalFieldType || 'text'} 
                onValueChange={(v) => setNewField({ ...newField, conditionalFieldType: v, conditionalFieldOptions: v === 'select' ? newField.conditionalFieldOptions : [] })}
              >
                <SelectTrigger className="bg-background border-border text-foreground h-8" data-testid="conditional-field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {CONDITIONAL_FIELD_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs text-foreground">Placeholder (optional)</Label>
            <Input
              value={newField.conditionalFieldPlaceholder || ''}
              onChange={(e) => setNewField({ ...newField, conditionalFieldPlaceholder: e.target.value })}
              placeholder="Enter placeholder text..."
              className="bg-background border-border text-foreground h-8"
              data-testid="conditional-field-placeholder"
            />
          </div>
          
          {/* Dropdown options for conditional field */}
          {newField.conditionalFieldType === 'select' && (
            <div className="space-y-2">
              <Label className="text-xs text-foreground">Dropdown Options</Label>
              <div className="flex gap-2">
                <Input
                  value={conditionalOptionInput}
                  onChange={(e) => setConditionalOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (conditionalOptionInput.trim()) {
                        setNewField({ ...newField, conditionalFieldOptions: [...(newField.conditionalFieldOptions || []), conditionalOptionInput.trim()] });
                        setConditionalOptionInput('');
                      }
                    }
                  }}
                  placeholder="Add option..."
                  className="bg-background border-border text-foreground flex-1 h-8"
                />
                <Button 
                  size="sm" 
                  onClick={() => {
                    if (conditionalOptionInput.trim()) {
                      setNewField({ ...newField, conditionalFieldOptions: [...(newField.conditionalFieldOptions || []), conditionalOptionInput.trim()] });
                      setConditionalOptionInput('');
                    }
                  }} 
                  type="button"
                  className="h-8"
                >
                  Add
                </Button>
              </div>
              {newField.conditionalFieldOptions?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newField.conditionalFieldOptions.map((opt, idx) => (
                    <span key={idx} className="px-2 py-1 bg-muted/30 rounded-lg text-xs flex items-center gap-1 text-foreground">
                      {opt}
                      <button 
                        onClick={() => setNewField({ ...newField, conditionalFieldOptions: newField.conditionalFieldOptions.filter((_, i) => i !== idx) })} 
                        className="text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Time Picker Info */}
      {newField.type === 'time' && (
        <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Time Picker</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Users can select a time using a standard time picker input.
          </p>
        </div>
      )}
      
      {/* 2-Column Layout Settings */}
      {newField.type === 'columns2' && (
        <div className="space-y-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
          <div className="flex items-center gap-2">
            <Columns2 className="w-4 h-4 text-purple-400" />
            <Label className="text-xs font-semibold text-purple-400">2-Column Layout Settings</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Display two input fields side by side. Each column can be a different field type.
          </p>
          
          {/* Column 1 */}
          <div className="p-2 bg-background/50 rounded-lg space-y-2">
            <Label className="text-xs font-medium text-purple-300">Column 1</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Label *</Label>
                <Input
                  value={newField.column1Label}
                  onChange={(e) => setNewField({ ...newField, column1Label: e.target.value })}
                  placeholder="e.g., Event Type"
                  className="bg-background border-border text-foreground h-8"
                  data-testid="columns2-col1-label"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Type</Label>
                <Select 
                  value={newField.column1Type || 'text'} 
                  onValueChange={(v) => setNewField({ ...newField, column1Type: v })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {COLUMN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.column1Type === 'select' && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs text-foreground">Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={column1OptionInput}
                    onChange={(e) => setColumn1OptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (column1OptionInput.trim()) {
                          setNewField({ ...newField, column1Options: [...(newField.column1Options || []), column1OptionInput.trim()] });
                          setColumn1OptionInput('');
                        }
                      }
                    }}
                    placeholder="Add option..."
                    className="bg-background border-border text-foreground flex-1 h-8"
                  />
                  <Button 
                    size="sm" 
                    type="button"
                    className="h-8"
                    onClick={() => {
                      if (column1OptionInput.trim()) {
                        setNewField({ ...newField, column1Options: [...(newField.column1Options || []), column1OptionInput.trim()] });
                        setColumn1OptionInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {newField.column1Options?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newField.column1Options.map((opt, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-500/20 rounded text-xs flex items-center gap-1 text-foreground">
                        {opt}
                        <button 
                          onClick={() => setNewField({ ...newField, column1Options: newField.column1Options.filter((_, i) => i !== idx) })} 
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Column 2 */}
          <div className="p-2 bg-background/50 rounded-lg space-y-2">
            <Label className="text-xs font-medium text-purple-300">Column 2</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Label *</Label>
                <Input
                  value={newField.column2Label}
                  onChange={(e) => setNewField({ ...newField, column2Label: e.target.value })}
                  placeholder="e.g., Details"
                  className="bg-background border-border text-foreground h-8"
                  data-testid="columns2-col2-label"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Type</Label>
                <Select 
                  value={newField.column2Type || 'text'} 
                  onValueChange={(v) => setNewField({ ...newField, column2Type: v })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {COLUMN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.column2Type === 'select' && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs text-foreground">Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={column2OptionInput}
                    onChange={(e) => setColumn2OptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (column2OptionInput.trim()) {
                          setNewField({ ...newField, column2Options: [...(newField.column2Options || []), column2OptionInput.trim()] });
                          setColumn2OptionInput('');
                        }
                      }
                    }}
                    placeholder="Add option..."
                    className="bg-background border-border text-foreground flex-1 h-8"
                  />
                  <Button 
                    size="sm" 
                    type="button"
                    className="h-8"
                    onClick={() => {
                      if (column2OptionInput.trim()) {
                        setNewField({ ...newField, column2Options: [...(newField.column2Options || []), column2OptionInput.trim()] });
                        setColumn2OptionInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {newField.column2Options?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newField.column2Options.map((opt, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-500/20 rounded text-xs flex items-center gap-1 text-foreground">
                        {opt}
                        <button 
                          onClick={() => setNewField({ ...newField, column2Options: newField.column2Options.filter((_, i) => i !== idx) })} 
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 3-Column Layout Settings */}
      {newField.type === 'columns3' && (
        <div className="space-y-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
          <div className="flex items-center gap-2">
            <Columns3 className="w-4 h-4 text-primary" />
            <Label className="text-xs font-semibold text-primary">3-Column Layout Settings</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Display three input fields side by side. Each column can be a different field type.
          </p>
          
          {/* Column 1 */}
          <div className="p-2 bg-background/50 rounded-lg space-y-2">
            <Label className="text-xs font-medium text-primary">Column 1</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Label *</Label>
                <Input
                  value={newField.column1Label}
                  onChange={(e) => setNewField({ ...newField, column1Label: e.target.value })}
                  placeholder="e.g., City"
                  className="bg-background border-border text-foreground h-8"
                  data-testid="columns3-col1-label"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Type</Label>
                <Select 
                  value={newField.column1Type || 'text'} 
                  onValueChange={(v) => setNewField({ ...newField, column1Type: v })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {COLUMN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.column1Type === 'select' && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs text-foreground">Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={column1OptionInput}
                    onChange={(e) => setColumn1OptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (column1OptionInput.trim()) {
                          setNewField({ ...newField, column1Options: [...(newField.column1Options || []), column1OptionInput.trim()] });
                          setColumn1OptionInput('');
                        }
                      }
                    }}
                    placeholder="Add option..."
                    className="bg-background border-border text-foreground flex-1 h-8"
                  />
                  <Button 
                    size="sm" 
                    type="button"
                    className="h-8"
                    onClick={() => {
                      if (column1OptionInput.trim()) {
                        setNewField({ ...newField, column1Options: [...(newField.column1Options || []), column1OptionInput.trim()] });
                        setColumn1OptionInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {newField.column1Options?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newField.column1Options.map((opt, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-primary/20 rounded text-xs flex items-center gap-1 text-foreground">
                        {opt}
                        <button 
                          onClick={() => setNewField({ ...newField, column1Options: newField.column1Options.filter((_, i) => i !== idx) })} 
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Column 2 */}
          <div className="p-2 bg-background/50 rounded-lg space-y-2">
            <Label className="text-xs font-medium text-primary">Column 2</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Label *</Label>
                <Input
                  value={newField.column2Label}
                  onChange={(e) => setNewField({ ...newField, column2Label: e.target.value })}
                  placeholder="e.g., State"
                  className="bg-background border-border text-foreground h-8"
                  data-testid="columns3-col2-label"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Type</Label>
                <Select 
                  value={newField.column2Type || 'text'} 
                  onValueChange={(v) => setNewField({ ...newField, column2Type: v })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {COLUMN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.column2Type === 'select' && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs text-foreground">Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={column2OptionInput}
                    onChange={(e) => setColumn2OptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (column2OptionInput.trim()) {
                          setNewField({ ...newField, column2Options: [...(newField.column2Options || []), column2OptionInput.trim()] });
                          setColumn2OptionInput('');
                        }
                      }
                    }}
                    placeholder="Add option..."
                    className="bg-background border-border text-foreground flex-1 h-8"
                  />
                  <Button 
                    size="sm" 
                    type="button"
                    className="h-8"
                    onClick={() => {
                      if (column2OptionInput.trim()) {
                        setNewField({ ...newField, column2Options: [...(newField.column2Options || []), column2OptionInput.trim()] });
                        setColumn2OptionInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {newField.column2Options?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newField.column2Options.map((opt, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-primary/20 rounded text-xs flex items-center gap-1 text-foreground">
                        {opt}
                        <button 
                          onClick={() => setNewField({ ...newField, column2Options: newField.column2Options.filter((_, i) => i !== idx) })} 
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Column 3 */}
          <div className="p-2 bg-background/50 rounded-lg space-y-2">
            <Label className="text-xs font-medium text-primary">Column 3</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Label *</Label>
                <Input
                  value={newField.column3Label}
                  onChange={(e) => setNewField({ ...newField, column3Label: e.target.value })}
                  placeholder="e.g., ZIP"
                  className="bg-background border-border text-foreground h-8"
                  data-testid="columns3-col3-label"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-foreground">Type</Label>
                <Select 
                  value={newField.column3Type || 'text'} 
                  onValueChange={(v) => setNewField({ ...newField, column3Type: v })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {COLUMN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newField.column3Type === 'select' && (
              <div className="space-y-2 mt-2">
                <Label className="text-xs text-foreground">Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={column3OptionInput}
                    onChange={(e) => setColumn3OptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (column3OptionInput.trim()) {
                          setNewField({ ...newField, column3Options: [...(newField.column3Options || []), column3OptionInput.trim()] });
                          setColumn3OptionInput('');
                        }
                      }
                    }}
                    placeholder="Add option..."
                    className="bg-background border-border text-foreground flex-1 h-8"
                  />
                  <Button 
                    size="sm" 
                    type="button"
                    className="h-8"
                    onClick={() => {
                      if (column3OptionInput.trim()) {
                        setNewField({ ...newField, column3Options: [...(newField.column3Options || []), column3OptionInput.trim()] });
                        setColumn3OptionInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {newField.column3Options?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newField.column3Options.map((opt, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-primary/20 rounded text-xs flex items-center gap-1 text-foreground">
                        {opt}
                        <button 
                          onClick={() => setNewField({ ...newField, column3Options: newField.column3Options.filter((_, i) => i !== idx) })} 
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Section / Page Break Info */}
      {newField.type === 'section' && (
        <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
          <div className="flex items-center gap-2">
            <SeparatorHorizontal className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-orange-500">Section / Page Break</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Creates a visual section divider. When printed, content after this will start on a new page.
          </p>
        </div>
      )}
      
      {/* Comment / Note Info */}
      {newField.type === 'comment' && (
        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-500">Comment / Note</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Adds descriptive text to help users. Not an input field - just informational content.
          </p>
        </div>
      )}
      
      {newField.type === 'repeater' && (
        <div className="space-y-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
          <Label className="text-xs font-semibold text-purple-400">2-Column Repeater Settings</Label>
          <p className="text-xs text-muted-foreground">
            Users can add multiple rows with 2 columns. Great for song lists, guest names, etc.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Column 1 Label *</Label>
              <Input
                value={newField.column1Label}
                onChange={(e) => setNewField({ ...newField, column1Label: e.target.value })}
                placeholder="e.g., Song Name"
                className="bg-background border-border text-foreground"
                data-testid="column1-label-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Column 2 Label *</Label>
              <Input
                value={newField.column2Label}
                onChange={(e) => setNewField({ ...newField, column2Label: e.target.value })}
                placeholder="e.g., Artist"
                className="bg-background border-border text-foreground"
                data-testid="column2-label-input"
              />
            </div>
          </div>
        </div>
      )}
      {newField.type === 'repeater3' && (
        <div className="space-y-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
          <Label className="text-xs font-semibold text-primary">3-Column Repeater Settings</Label>
          <p className="text-xs text-muted-foreground">
            Users can add multiple rows with 3 columns. Great for timelines, schedules, etc.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Column 1 Label *</Label>
              <Input
                value={newField.column1Label}
                onChange={(e) => setNewField({ ...newField, column1Label: e.target.value })}
                placeholder="e.g., Time"
                className="bg-background border-border text-foreground"
                data-testid="column1-label-input-3col"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Column 2 Label *</Label>
              <Input
                value={newField.column2Label}
                onChange={(e) => setNewField({ ...newField, column2Label: e.target.value })}
                placeholder="e.g., Activity"
                className="bg-background border-border text-foreground"
                data-testid="column2-label-input-3col"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-foreground">Column 3 Label *</Label>
              <Input
                value={newField.column3Label}
                onChange={(e) => setNewField({ ...newField, column3Label: e.target.value })}
                placeholder="e.g., Notes"
                className="bg-background border-border text-foreground"
                data-testid="column3-label-input"
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        {newField.type !== 'section' && newField.type !== 'comment' && (
          <label className="flex items-center gap-2 cursor-pointer text-foreground">
            <input
              type="checkbox"
              checked={newField.required}
              onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
              className="rounded"
              data-testid="field-required-checkbox"
            />
            <span className="text-sm">Required field</span>
          </label>
        )}
        {(newField.type === 'section' || newField.type === 'comment') && <div />}
        <Button onClick={() => addField(isEditing)} size="sm" data-testid="add-field-btn">
          <Plus className="w-4 h-4 mr-1" />
          Add Field
        </Button>
      </div>
    </div>
  );

  const renderFieldsList = (fields, isEditing = false) => {
    // Ensure each field has a unique ID for drag-and-drop
    const fieldsWithIds = fields.map((field, index) => ({
      ...field,
      id: field.id || `field-${index}`
    }));

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, isEditing)}
      >
        <SortableContext
          items={fieldsWithIds.map(f => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2 mt-4">
            {fieldsWithIds.map((field, index) => (
              <SortableFieldItem
                key={field.id}
                field={field}
                index={index}
                isEditing={isEditing}
                onRemove={(idx) => removeField(idx, isEditing)}
                onEdit={(idx) => startEditField(idx, isEditing)}
                editingFieldIndex={editingFieldIndex}
                setEditingFieldIndex={setEditingFieldIndex}
                onSaveEdit={(idx) => saveFieldEdit(idx, isEditing)}
                editFieldData={editFieldData}
                setEditFieldData={setEditFieldData}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="forms-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="forms-heading">Forms</h1>
              <p className="text-muted-foreground">Create and customize forms for your website and clients</p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-form-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Form
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-form-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">Create New Form</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Form Name *</Label>
                    <Input
                      value={newForm.name}
                      onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                      placeholder="e.g., Song Request Form"
                      className="bg-background border-border text-foreground"
                      data-testid="form-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Description</Label>
                    <Input
                      value={newForm.description}
                      onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                      placeholder="Brief description of the form"
                      className="bg-background border-border text-foreground"
                      data-testid="form-description-input"
                    />
                  </div>

                  {/* Lockout Settings */}
                  <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30 space-y-3">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-orange-500" />
                      <span className="font-medium text-orange-500">Client Portal Lockout</span>
                      <span className="text-xs text-muted-foreground">(Optional)</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Restrict when clients can edit this form relative to their event date. 
                      <span className="text-orange-400 font-medium"> Leave both fields empty for no lockout - form will always be viewable by clients.</span>
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-foreground">Days Before Event</Label>
                        <Input
                          type="number"
                          min="0"
                          value={newForm.lockout_days_before_event}
                          onChange={(e) => setNewForm({ ...newForm, lockout_days_before_event: e.target.value })}
                          placeholder="e.g., 7"
                          className="bg-background border-border text-foreground h-9"
                          data-testid="lockout-before-input"
                        />
                        <p className="text-xs text-muted-foreground">Lock form this many days before event</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-foreground">Days After Event</Label>
                        <Input
                          type="number"
                          min="0"
                          value={newForm.lockout_days_after_event}
                          onChange={(e) => setNewForm({ ...newForm, lockout_days_after_event: e.target.value })}
                          placeholder="e.g., 0"
                          className="bg-background border-border text-foreground h-9"
                          data-testid="lockout-after-input"
                        />
                        <p className="text-xs text-muted-foreground">Lock form this many days after event</p>
                      </div>
                    </div>
                  </div>

                  {renderFieldEditor(false)}
                  
                  {newForm.fields.length > 0 && (
                    <>
                      <Label className="block mt-4">Fields ({newForm.fields.length})</Label>
                      {renderFieldsList(newForm.fields, false)}
                    </>
                  )}

                  <Button
                    onClick={handleCreate}
                    className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black mt-4"
                    data-testid="form-create-submit-btn"
                  >
                    Create Form
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="my-forms" data-testid="tab-my-forms">
                  My Forms ({forms.length})
                </TabsTrigger>
                <TabsTrigger value="shared-forms" data-testid="tab-shared-forms">
                  <Share2 className="w-4 h-4 mr-1" />
                  Shared Library ({sharedForms.length})
                </TabsTrigger>
              </TabsList>

              {/* My Forms Tab */}
              <TabsContent value="my-forms">
                {forms.length === 0 ? (
                  <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-forms-message">
                    <p className="text-muted-foreground">No forms yet. Create your first form!</p>
                  </div>
                ) : (
                  <div className="grid gap-4" data-testid="forms-list">
                    {forms.map((form) => (
                      <div
                        key={form.id}
                        className={`glass-card-hover rounded-2xl p-6 ${form.form_type === 'contact' ? 'border-l-4 border-l-neon-blue' : ''}`}
                        data-testid={`form-item-${form.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {form.form_type === 'contact' ? (
                                <Globe className="w-5 h-5 text-neon-blue" />
                              ) : (
                                <ClipboardList className="w-5 h-5 text-muted-foreground" />
                              )}
                              <h3 className="font-heading text-xl font-semibold">{form.name}</h3>
                              {form.is_system && (
                                <span className="text-xs px-2 py-0.5 bg-neon-blue/20 text-neon-blue rounded-full">System</span>
                              )}
                              {form.is_shared && (
                                <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded-full flex items-center gap-1">
                                  <Share2 className="w-3 h-3" /> Shared
                                </span>
                              )}
                              {form.imported_from && (
                                <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-500 rounded-full flex items-center gap-1">
                                  <Download className="w-3 h-3" /> Imported
                                </span>
                              )}
                            </div>
                            {form.description && (
                              <p className="text-sm text-muted-foreground mb-2 ml-8">{form.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-muted-foreground ml-8">
                              <span>{form.fields?.length || 0} field(s)</span>
                              {form.created_by_company && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" /> {form.created_by_company}
                                </span>
                              )}
                              {form.original_company && (
                                <span className="flex items-center gap-1 text-purple-400">
                                  From: {form.original_company}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {formatDate(form.created_at)}
                              </span>
                              {form.updated_at && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Updated: {formatDate(form.updated_at)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Share Toggle - only for non-system, non-contact forms */}
                            {!form.is_system && form.form_type !== 'contact' && (
                              <div className="flex items-center gap-2 mr-2 px-3 py-1 rounded-lg bg-muted/30">
                                <Label className="text-xs text-muted-foreground">Share</Label>
                                <Switch
                                  checked={form.is_shared}
                                  onCheckedChange={(checked) => toggleFormSharing(form.id, checked)}
                                  data-testid={`share-toggle-${form.id}`}
                                />
                              </div>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPreview(form)}
                              className="border-border hover:bg-muted/50"
                              data-testid={`view-form-btn-${form.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(form)}
                              className="border-border hover:bg-muted/50"
                              data-testid={`edit-form-btn-${form.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {!form.is_system && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(form.id)}
                                className="text-red-500 hover:text-red-400"
                                data-testid={`delete-form-btn-${form.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Shared Forms Library Tab */}
              <TabsContent value="shared-forms">
                {sharedForms.length === 0 ? (
                  <div className="text-center py-12 glass-card rounded-3xl">
                    <Share2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No shared forms available from other companies yet.</p>
                    <p className="text-sm text-muted-foreground mt-2">Share your forms to contribute to the library!</p>
                  </div>
                ) : (
                  <div className="grid gap-4" data-testid="shared-forms-list">
                    {sharedForms.map((form) => (
                      <div
                        key={form.id}
                        className="glass-card-hover rounded-2xl p-6 border-l-4 border-l-green-500"
                        data-testid={`shared-form-item-${form.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <ClipboardList className="w-5 h-5 text-green-500" />
                              <h3 className="font-heading text-xl font-semibold">{form.name}</h3>
                            </div>
                            {form.description && (
                              <p className="text-sm text-muted-foreground mb-2 ml-8">{form.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-muted-foreground ml-8">
                              <span>{form.fields?.length || 0} field(s)</span>
                              {form.created_by_company && (
                                <span className="flex items-center gap-1 text-green-400">
                                  <Building2 className="w-3 h-3" /> {form.created_by_company}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {formatDate(form.created_at)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPreview(form)}
                              className="border-border hover:bg-muted/50"
                              data-testid={`preview-shared-form-${form.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => importSharedForm(form.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`import-form-${form.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Import to My Forms
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Edit Form Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2 text-foreground">
              {editingForm?.form_type === 'contact' && <Globe className="w-5 h-5 text-primary" />}
              Edit {editingForm?.name}
            </DialogTitle>
            <DialogDescription>
              {editingForm?.form_type === 'contact' 
                ? 'Customize the fields shown on your website contact form'
                : 'Modify the form fields and settings'
              }
            </DialogDescription>
          </DialogHeader>
          {editingForm && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-foreground">Form Name</Label>
                <Input
                  value={editingForm.name}
                  onChange={(e) => setEditingForm({ ...editingForm, name: e.target.value })}
                  className="bg-background border-border text-foreground"
                  data-testid="edit-form-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Description</Label>
                <Input
                  value={editingForm.description || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, description: e.target.value })}
                  className="bg-background border-border text-foreground"
                  data-testid="edit-form-description-input"
                />
              </div>

              {/* Lockout Settings */}
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30 space-y-3">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-orange-500" />
                  <span className="font-medium text-orange-500">Client Portal Lockout</span>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Restrict when clients can edit this form relative to their event date.
                  <span className="text-orange-400 font-medium"> Leave both fields empty for no lockout - form will always be viewable by clients.</span>
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-foreground">Days Before Event</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingForm.lockout_days_before_event || ''}
                      onChange={(e) => setEditingForm({ ...editingForm, lockout_days_before_event: e.target.value })}
                      placeholder="e.g., 7"
                      className="bg-background border-border text-foreground h-9"
                      data-testid="edit-lockout-before-input"
                    />
                    <p className="text-xs text-muted-foreground">Lock form this many days before event</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-foreground">Days After Event</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingForm.lockout_days_after_event || ''}
                      onChange={(e) => setEditingForm({ ...editingForm, lockout_days_after_event: e.target.value })}
                      placeholder="e.g., 0"
                      className="bg-background border-border text-foreground h-9"
                      data-testid="edit-lockout-after-input"
                    />
                    <p className="text-xs text-muted-foreground">Lock form this many days after event</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-foreground">Current Fields ({editingForm.fields?.length || 0})</Label>
                  <p className="text-xs text-muted-foreground">Drag to reorder • Click pencil to edit</p>
                </div>
                {editingForm.fields?.length > 0 ? (
                  renderFieldsList(editingForm.fields, true)
                ) : (
                  <p className="text-muted-foreground text-sm py-4 text-center border border-dashed border-border rounded-lg">No fields yet. Add fields below.</p>
                )}
              </div>

              <div className="border-t border-border pt-4">
                {renderFieldEditor(true)}
              </div>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => openPreview(editingForm)} className="border-border">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-border">
                  Cancel
                </Button>
                <Button onClick={handleUpdate} className="bg-neon-blue hover:bg-neon-blue/80 text-black">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preview-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2 text-foreground">
              <Eye className="w-6 h-6 text-primary" />
              {previewForm?.name}
            </DialogTitle>
            {previewForm?.description && (
              <DialogDescription>{previewForm.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border">
            <p className="text-xs text-muted-foreground mb-4 text-center">Form Preview - This is how clients will see the form</p>
            {previewForm?.fields?.length > 0 ? (
              renderFormPreview(previewForm)
            ) : (
              <p className="text-center text-muted-foreground py-8">No fields in this form</p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)} className="border-border">
              Close
            </Button>
            <Button 
              onClick={() => { setShowPreviewDialog(false); openEditDialog(previewForm); }} 
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
